<template>
  <div class="min-h-screen bg-gray-50 flex flex-col">
    <!-- Same Hero as HomePage -->
    <section class="relative overflow-hidden bg-gradient-to-br from-emerald-500 via-teal-500 to-fuchsia-600 py-12 md:py-16">
      <div class="absolute inset-0">
        <img
          src="https://images.ctfassets.net/zhgoh1a85x2k/7qiZ6sqBkl7IS1AXUA5j4T/d3ab432178763cbc8f0a053c4bbb320b/mtu-image.webp"
          alt=""
          class="w-full h-full object-cover object-center"
        >
      </div>
      <div class="absolute inset-0 bg-gradient-to-r from-gray-900/90 via-gray-900/60 to-transparent"></div>

      <div class="relative z-10 px-4 sm:px-8 md:px-12 lg:px-16 xl:px-24 2xl:px-32 pt-[80px] md:pt-[120px] pb-12">
        <div class="flex flex-col md:flex-row items-start md:items-center gap-8 max-w-6xl mx-auto">
          <div class="text-white md:w-1/2">
            <p class="text-emerald-400 font-semibold tracking-widest uppercase text-sm mb-4">
              Recherche
            </p>
            <h1 class="text-4xl lg:text-6xl xl:text-7xl font-bold leading-tight mb-4">
              Trouvez<br>
              <span class="text-transparent bg-clip-text bg-gradient-to-r from-fuchsia-400 to-amber-400">
                tout ce qu'il vous faut
              </span>
            </h1>
            <p class="text-lg lg:text-xl text-white/80">
              Recharge mobile, cartes cadeaux, eSim et plus encore
            </p>
          </div>

          <!-- Search Box -->
          <div class="w-full md:w-[400px] md:ml-auto backdrop-blur-xl">
            <div class="bg-white/10 border border-white/20 rounded-3xl p-6 shadow-2xl">
              <p class="text-white font-semibold mb-4 flex items-center gap-2">
                <svg class="w-5 h-5 text-fuchsia-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
                </svg>
                Votre recherche
              </p>

              <input
                v-model="searchQuery"
                type="text"
                placeholder="Netflix, Amazon, MTN..."
                class="w-full px-5 py-4 bg-white border-0 rounded-2xl text-gray-900 font-semibold placeholder:text-gray-400 focus:outline-none focus:ring-4 focus:ring-fuchsia-400/30 transition-all"
              >

              <p v-if="searchQuery" class="text-white/70 text-sm mt-3">
                {{ totalResults }} résultat(s) trouvé(s)
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Results Sections - same as HomePage but with v-show -->
    <main class="flex-1 py-12">
      <div class="max-w-[1600px] mx-auto px-4 sm:px-8 md:px-12 lg:px-16 xl:px-24">
        <!-- Sections from categorySections -->
        <section
          v-for="section in categorySections"
          :key="section.key"
          v-show="!searchQuery || sectionHasResults(section)"
          class="mb-12"
        >
          <div class="flex items-center justify-between mb-6">
            <div>
              <h2 class="text-2xl font-bold text-gray-900">{{ section.title }}</h2>
              <p class="text-gray-500 mt-1">{{ section.products.length }} produit(s)</p>
            </div>
            <router-link
              v-if="section.link && !searchQuery"
              :to="section.link"
              class="text-fuchsia-600 hover:text-fuchsia-700 font-medium text-sm flex items-center gap-1"
            >
              Voir tout
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
              </svg>
            </router-link>
          </div>

          <div class="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            <button
              v-for="product in section.products"
              :key="product.id"
              v-show="!searchQuery || matchSearch(product)"
              @click="handleProductClick(product, section)"
              class="flex flex-col gap-2 cursor-pointer group"
            >
              <div class="aspect-square bg-white rounded-2xl overflow-hidden shadow-[0_0_4px_rgba(0,0,0,0.25)] transition-transform group-hover:scale-105"
                   :class="{ 'aspect-[188/148]': section.key !== 'mobile' && section.key !== 'esim' }">
                <img
                  v-if="product.image"
                  :alt="product.name"
                  loading="lazy"
                  :src="product.image"
                  class="w-full h-full object-contain p-4"
                >
                <div v-else class="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200">
                  <span class="text-4xl">📱</span>
                </div>
              </div>
              <span class="text-sm font-medium text-gray-700 text-center">{{ product.name }}</span>
            </button>
          </div>
        </section>

        <!-- No Results -->
        <div v-if="totalResults === 0 && searchQuery" class="text-center py-20">
          <div class="text-7xl mb-6">🔍</div>
          <h3 class="text-2xl font-bold text-gray-900 mb-3">
            Aucun résultat
          </h3>
          <p class="text-gray-600 text-lg">
            Essayez d'autres mots-clés pour votre recherche
          </p>
        </div>

        <!-- Initial State -->
        <div v-if="!searchQuery" class="text-center py-20">
          <div class="text-7xl mb-6">🔍</div>
          <h3 class="text-2xl font-bold text-gray-900 mb-3">
            Lancez une recherche
          </h3>
          <p class="text-gray-600 text-lg">
            Entrez un nom de produit, une marque, ou un montant ci-dessus
          </p>
        </div>
      </div>
    </main>

  </div>
</template>

<script setup>
import { ref, computed, onMounted, getCurrentInstance, watch } from 'vue';
import { useRouter } from 'vue-router';
import { Countries } from '@features/shared/libraries/countries';

const router = useRouter();
const { proxy } = getCurrentInstance();
const { $stores, $constants } = proxy;

const searchQuery = ref('');
const mobileOperators = ref([]);
const giftCards = ref([]);
const esimPackages = ref([]);

// On mounted, check if there's a search query from window
onMounted(() => {
  if (window.searchQuery) {
    searchQuery.value = window.searchQuery;
    window.searchQuery = '';
  }
});

const fetchData = async () => {
  const country = $stores.global.state.selectedCountry;
  if (!country) return;

  const countryCode = country.countryCode;
  const { bus } = await import('@core/events/eventSystem');
  const constants = await import('@features/esim/constants');

  try {
    // Fetch mobile operators using products.fetch_operators
    await new Promise((resolve, reject) => {
      const opSuccessHandler = (data) => {
        bus.off('products.fetch_operators_success', opSuccessHandler);
        bus.off('products.fetch_operators_error', opErrorHandler);
        const operators = data.data?.operatorList || data.data || data || [];
        mobileOperators.value = operators.map(op => ({
          id: op.operatorId,
          name: op.name || op.operatorName,
          image: op.logoUrls?.[1] || op.logoUrls?.[0] || '',
          data: op
        }));
        resolve();
      };

      const opErrorHandler = (err) => {
        bus.off('products.fetch_operators_success', opSuccessHandler);
        bus.off('products.fetch_operators_error', opErrorHandler);
        mobileOperators.value = [];
        resolve();
      };

      bus.on('products.fetch_operators_success', opSuccessHandler);
      bus.on('products.fetch_operators_error', opErrorHandler);
      bus.emit('products.fetch_operators', {
        countryCode,
        type: 'airtime'
      });
    });

    // Fetch gift cards
    await new Promise((resolve, reject) => {
      const cardSuccessHandler = (data) => {
        bus.off('cards.fetch_by_country_success', cardSuccessHandler);
        bus.off('cards.fetch_by_country_error', cardErrorHandler);
        const products = data.data || data;

        giftCards.value = products.filter(p => !p.operatorId && p.type !== 'MOBILE_OPERATOR').map(p => ({
          id: p.productId,
          name: p.productName,
          image: p.logoUrls?.[0] || '',
          category: p.category,
          data: p
        }));

        resolve();
      };

      const cardErrorHandler = (error) => {
        bus.off('cards.fetch_by_country_success', cardSuccessHandler);
        bus.off('cards.fetch_by_country_error', cardErrorHandler);
        giftCards.value = [];
        resolve();
      };

      bus.on('cards.fetch_by_country_success', cardSuccessHandler);
      bus.on('cards.fetch_by_country_error', cardErrorHandler);
      bus.emit('cards.fetch_by_country', countryCode);
    });

    // Fetch eSim packages using event bus
    const locationCode = getEsimLocationCode(countryCode);

    await new Promise((resolve, reject) => {
      const esimSuccessHandler = (data) => {
        bus.off(constants.EVENTS.ESIM.FETCH_PACKAGES_SUCCESS, esimSuccessHandler);
        bus.off(constants.EVENTS.ESIM.FETCH_PACKAGES_ERROR, esimErrorHandler);
        esimPackages.value = data || [];
        resolve();
      };

      const esimErrorHandler = (err) => {
        bus.off(constants.EVENTS.ESIM.FETCH_PACKAGES_SUCCESS, esimSuccessHandler);
        bus.off(constants.EVENTS.ESIM.FETCH_PACKAGES_ERROR, esimErrorHandler);
        esimPackages.value = [];
        resolve();
      };

      bus.on(constants.EVENTS.ESIM.FETCH_PACKAGES_SUCCESS, esimSuccessHandler);
      bus.on(constants.EVENTS.ESIM.FETCH_PACKAGES_ERROR, esimErrorHandler);
      bus.emit(constants.EVENTS.ESIM.FETCH_PACKAGES, locationCode);
    });
  } catch (error) {
    console.error('Error fetching search data:', error);
  }
};

// Map country code to esim location code
const getEsimLocationCode = (countryCode) => {
  const mapping = {
    'BJ': 'BJ', 'TG': 'TG', 'CI': 'CI', 'SN': 'SN', 'ML': 'ML',
    'FR': 'FR', 'BE': 'BE', 'CH': 'CH', 'CM': 'CM', 'CD': 'CD',
    'NE': 'NE', 'BF': 'BF', 'GN': 'GN', 'GA': 'GA', 'CG': 'CG',
    'CF': 'CF', 'TD': 'TD', 'MG': 'MG', 'RW': 'RW', 'BI': 'BI',
    'DJ': 'DJ', 'ER': 'ER', 'ET': 'ET', 'KE': 'KE', 'MG': 'MG',
    'MW': 'MW', 'MU': 'MU', 'MZ': 'MZ', 'NA': 'NA', 'RW': 'RW',
    'SC': 'SC', 'TZ': 'TZ', 'UG': 'UG', 'ZM': 'ZM', 'ZW': 'ZW',
    'AO': 'AO', 'NA': 'NA', 'ZA': 'ZA', 'EG': 'EG', 'MA': 'MA',
    'TN': 'TN', 'DZ': 'DZ', 'LY': 'LY', 'NG': 'NG', 'GH': 'GH',
    'CI': 'CI', 'SN': 'SN', 'ML': 'ML', 'BF': 'BF', 'NE': 'NE',
    'TD': 'TD', 'CF': 'CF', 'CD': 'CD', 'CG': 'CG', 'GA': 'GA',
    'CM': 'CM', 'LR': 'LR', 'SL': 'SL', 'GN': 'GN', 'GW': 'GW',
    'GM': 'GM', 'MR': 'MR', 'SO': 'SO', 'SD': 'SD', 'ET': 'ET',
    'KE': 'KE', 'UG': 'UG', 'TZ': 'TZ', 'RW': 'RW', 'BI': 'BI',
    'MW': 'MW', 'ZM': 'ZM', 'MZ': 'MZ', 'ZW': 'ZW', 'BW': 'BW',
    'NA': 'NA', 'SZ': 'SZ', 'LS': 'LS', 'ZA': 'ZA'
  };
  return mapping[countryCode] || countryCode;
};

// Watch for country changes and fetch data (immediate to fetch on mount)
watch(() => $stores.global.state.selectedCountry?.countryCode, async (newCountryCode) => {
  if (newCountryCode) {
    await fetchData();
  }
}, { immediate: true });

// Category sections - same as HomePage but without slice limit
const categorySections = computed(() => [
  {
    key: 'mobile',
    title: 'Recharge mobile',
    link: '/mobile-top-up',
    products: mobileOperators.value.map(op => ({
      id: op.id,
      name: op.name,
      image: op.image,
      data: op.data
    }))
  },
  {
    key: 'payment',
    title: 'Cartes de paiement',
    link: '/cards/payment-cards',
    products: giftCards.value
      .filter(p => p.data?.category?.id === 1)
      .map(p => ({
        id: p.id,
        name: p.name,
        image: p.image,
        data: p.data
      }))
  },
  {
    key: 'entertainment',
    title: 'Divertissement',
    link: '/cards/entertainment-gift-cards',
    products: giftCards.value
      .filter(p => p.data?.category?.id === 4)
      .map(p => ({
        id: p.id,
        name: p.name,
        image: p.image,
        data: p.data
      }))
  },
  {
    key: 'shopping',
    title: 'Shopping',
    link: '/cards/shopping-gift-cards',
    products: giftCards.value
      .filter(p => p.data?.category?.id === 5)
      .map(p => ({
        id: p.id,
        name: p.name,
        image: p.image,
        data: p.data
      }))
  },
  {
    key: 'gaming',
    title: 'Jeux vidéo',
    link: '/cards/gaming-cards',
    products: giftCards.value
      .filter(p => p.data?.category?.id === 3)
      .map(p => ({
        id: p.id,
        name: p.name,
        image: p.image,
        data: p.data
      }))
  },
  {
    key: 'other',
    title: 'Autres cartes',
    link: '/cards/other-cards',
    products: giftCards.value
      .filter(p => {
        const mainCategoryIds = [1, 3, 4, 5];
        return !mainCategoryIds.includes(p.data?.category?.id);
      })
      .map(p => ({
        id: p.id,
        name: p.name,
        image: p.image,
        data: p.data
      }))
  },
  {
    key: 'esim',
    title: 'eSim - Voyagez connecté',
    link: '/esim',
    products: esimPackages.value.map(pkg => ({
      id: pkg.packageCode,
      name: pkg.name || `${pkg.volume} - ${pkg.duration}`,
      image: '',
      data: pkg
    }))
  },
]);

// Match search function
const matchSearch = (product) => {
  if (!searchQuery.value) return true;
  const searchLower = searchQuery.value.toLowerCase();

  const searchableText = [
    product.name,
    product.data?.description || '',
    product.data?.brand?.brandName || '',
    product.data?.productName || '',
    String(product.data?.fixedRecipientDenominations?.[0] || ''),
    String(product.data?.minRecipientDenomination || ''),
    String(product.data?.maxRecipientDenomination || ''),
    product.data?.volume || '',
    product.data?.duration || ''
  ].join(' ').toLowerCase();

  return searchableText.includes(searchLower);
};

// Check if section has matching results
const sectionHasResults = (section) => {
  if (!searchQuery.value) return true;
  return section.products.some(p => matchSearch(p));
};

// Total results count
const totalResults = computed(() => {
  if (!searchQuery.value) return 0;
  return categorySections.value.reduce((total, section) => {
    return total + section.products.filter(p => matchSearch(p)).length;
  }, 0);
});

// Category slug mapping for gift cards
const categorySlugMap = {
  'payment': 'payment-cards',
  'shopping': 'shopping-gift-cards',
  'gaming': 'gaming-cards',
  'entertainment': 'entertainment-gift-cards'
};

const handleProductClick = (product, section) => {
  const country = $stores.global.state.selectedCountry;

  switch (section.key) {
    case 'mobile':
      // Mobile top-up: /mobile-top-up/{operatorId}-{countryCode}
      router.push({
        path: `/mobile-top-up/${product.id}-${country?.countryCode || 'BJ'}`
      });
      break;
    case 'payment':
    case 'shopping':
    case 'gaming':
    case 'entertainment':
    case 'other':
      // Gift cards: /cards/{categorySlug}/{productId}-{countryCode}
      const categorySlug = categorySlugMap[section.key] || 'other-cards';
      const slug = `${product.id}-${country?.countryCode?.toLowerCase() || 'bj'}`;
      router.push(`/cards/${categorySlug}/${slug}`);
      break;
    case 'esim':
      // eSim: /esim/{packageCode}-{countryCode}
      const esimSlug = `${product.id}-${country?.countryCode?.toLowerCase() || 'bj'}`;
      router.push(`/esim/${esimSlug}`);
      break;
    default:
      if (product.data?.slug) {
        router.push(`/cards/${product.data.slug}`);
      }
  }
};

// Note: We don't need to watch route.query.q back to searchQuery because
// searchQuery is initialized from the route and updates the route bidirectionally via v-model-like behavior
</script>
